import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../chart/bodygraph/hd-content-language.service';
import { HdLineTextReviewAccessService } from './hd-line-text-review-access.service';
import { HdLineTextReviewService } from './hd-line-text-review.service';
import { HdLineTextField, HdLineTextLang, HdLineTextReview, hdLineTextReviewKey } from './hd-line-text-review.metadata';

/**
 * Eén eigen lijntekst (Mechanics in Motion): het amber ◆ met de tekst erachter,
 * waarbij een in de app herschreven versie de brontekst uit de scope vervangt.
 *
 * Met de reviewfeature (én de setting "eigen lijnteksten") staan er een vinkje
 * (goedkeuren / intrekken) en een potlood achter; bewerken gebeurt ter plekke in
 * een tekstvak met opslaan (Ctrl+Enter) en annuleren (Escape). Opslaan bewaart de
 * tekst en keurt ze meteen goed. De kleur komt van de host (blauw voor de
 * lijnbeschrijving, gewone tekstkleur voor de exaltation-/detriment-regel).
 *
 * Twee reviewers tegelijk: de editor onthoudt het tijdstip van het record bij het
 * openen en geeft dat mee bij het opslaan. Was iemand anders eerst, dan weigert de
 * server en toont de editor de nieuwe versie met de keuze overnemen of toch
 * overschrijven. Komt tijdens het bewerken live een wijziging binnen, dan meldt
 * een banner dat al vóór het opslaan.
 */
@Component({
  selector: 'hd-own-line-text',
  templateUrl: './hd-own-line-text.component.html',
  styleUrl: './hd-own-line-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Het tekstvak groeit mee met de tekst (2 tot 9 regels), daarboven scrollt het.
  imports: [CdkTextareaAutosize],
  host: {
    '[class.hd-own-line-text--approved]': 'approved()',
    '[class.hd-own-line-text--editing]': 'editing()',
  },
})
export class HdOwnLineTextComponent {
  readonly gate = input.required<number>();
  readonly line = input.required<number>();
  readonly field = input.required<HdLineTextField>();
  /** Brontekst uit de scope `gate-lines-own` (in de effectieve content-taal). */
  readonly text = input.required<string>();

  private readonly review = inject(HdLineTextReviewService);
  private readonly access = inject(HdLineTextReviewAccessService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly textarea = viewChild<ElementRef<HTMLTextAreaElement>>('editor');

  /** De scope kent enkel nl en en; elke andere app-taal leest de Engelse tekst. */
  protected readonly lang = computed<HdLineTextLang>(() => (this.contentLang.effectiveLang() === 'nl' ? 'nl' : 'en'));

  protected readonly record = computed(() => this.review.record(this.gate(), this.line(), this.lang(), this.field()));
  protected readonly effectiveText = computed(() => this.record()?.text ?? this.text());
  protected readonly approved = computed(() => this.record()?.approved ?? false);
  protected readonly edited = computed(() => this.record()?.text != null);
  protected readonly canReview = this.access.reviewVisible;
  protected readonly busy = computed(() => this.review.isSaving(this.gate(), this.line(), this.lang(), this.field()));

  protected readonly editing = signal(false);
  protected readonly draft = signal('');
  /** Actuele stand van de server na een geweigerde opslag (iemand anders was eerst). */
  protected readonly conflict = signal<HdLineTextReview | null>(null);
  /** Wijziging van een andere reviewer die tijdens het bewerken live binnenkwam. */
  protected readonly remoteNotice = signal<HdLineTextReview | null>(null);
  /** Tijdstip van het record toen de editor openging; de server vergelijkt ermee. */
  private editBase: string | null = null;
  private lastSeenRemoteSequence = 0;

  protected readonly reviewerTitle = computed(() => {
    const record = this.record();
    if (!record) {
      return '';
    }
    const key = record.text != null ? 'humanDesign.chart.lineTextReview.editedBy' : 'humanDesign.chart.lineTextReview.approvedBy';
    return this.t(key, { name: record.updatedByDisplayName ?? '?' });
  });

  constructor() {
    // Doorklik vanuit het overzicht: de lijnbeschrijving van de gevraagde lijn
    // scrollt zichzelf in beeld zodra ze rendert.
    effect(() => {
      if (this.field() !== 'description' || !this.review.consumeFocus(this.gate(), this.line())) {
        return;
      }
      setTimeout(() => this.host.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    });
    // Tekstvak meteen klaar voor invoer zodra het opengaat.
    effect(() => {
      const editor = this.textarea()?.nativeElement;
      if (this.editing() && editor) {
        editor.focus();
        editor.setSelectionRange(editor.value.length, editor.value.length);
      }
    });
    // Live wijziging van een andere reviewer aan precies deze tekst terwijl de editor open staat.
    effect(() => {
      const change = this.review.lastRemoteChange();
      if (!change || change.sequence === this.lastSeenRemoteSequence) {
        return;
      }
      this.lastSeenRemoteSequence = change.sequence;
      if (this.editing() && change.key === this.key()) {
        this.remoteNotice.set(change.review);
      }
    });
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.contentLang.effectiveLang());
  }

  protected toggleApproved(event: Event): void {
    event.stopPropagation();
    void this.review.setApproved(this.gate(), this.line(), this.lang(), this.field(), !this.approved());
  }

  protected startEdit(event: Event): void {
    event.stopPropagation();
    this.draft.set(this.effectiveText());
    this.editBase = this.record()?.updatedOn ?? null;
    this.conflict.set(null);
    this.remoteNotice.set(null);
    this.editing.set(true);
  }

  /** Zet de brontekst uit de scope terug in het tekstvak (opslaan maakt dat definitief). */
  protected restoreSource(): void {
    this.draft.set(this.text());
  }

  protected cancelEdit(): void {
    this.editing.set(false);
    this.conflict.set(null);
    this.remoteNotice.set(null);
  }

  protected async save(): Promise<void> {
    if (this.draft().trim().length === 0) {
      return;
    }
    const outcome = await this.review.saveText(this.gate(), this.line(), this.lang(), this.field(), this.draft(), this.text(), this.editBase);
    if (outcome.status === 'saved') {
      this.cancelEdit();
    } else if (outcome.status === 'conflict') {
      this.remoteNotice.set(null);
      this.conflict.set(outcome.current);
    }
  }

  /** Na een botsing: de versie van de ander in het tekstvak nemen en vanaf daar verder werken. */
  protected adoptOther(other: HdLineTextReview): void {
    this.draft.set(other.text ?? this.text());
    this.editBase = other.updatedOn;
    this.conflict.set(null);
    this.remoteNotice.set(null);
  }

  /** Na een botsing: bewust overschrijven, met het tijdstip van de versie van de ander. */
  protected async overwriteOther(other: HdLineTextReview): Promise<void> {
    this.editBase = other.updatedOn;
    this.conflict.set(null);
    await this.save();
  }

  protected dismissRemoteNotice(): void {
    this.remoteNotice.set(null);
  }

  protected onEditorKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void this.save();
    }
  }

  private key(): string {
    return hdLineTextReviewKey(this.gate(), this.line(), this.lang(), this.field());
  }
}
